/**
 * HtmlBridgeEditor -- configuration UI for the HTML Bridge theme.
 *
 * Lets the capsule creator:
 *  - Upload a local .html file -- its raw source is saved to
 *    behaviorSettings.customHtml (+ customHtmlFileName), so it persists with
 *    the capsule across create -> preview -> play, no hosting required.
 *  - Or enter a URL to a hosted HTML theme (saved to behaviorSettings.htmlThemeUrl)
 *  - Preview the theme in a sandboxed iframe
 *
 * customHtml takes priority over htmlThemeUrl when both are present.
 */

import {useEffect, useRef, useState} from 'react'
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'

import {BUILTIN_HTML_THEMES, BUILTIN_THEME_FILENAME_MAP} from '#/lib/htmlThemes'
import {type ThemeEditorProps} from '#/types/capsule'
import {mapSlide} from './HtmlBridgePlayer'

/** Opens a file picker and resolves with the raw text content + file name. */
function pickHtmlFileTextWeb(): Promise<{
  text: string
  fileName: string
} | null> {
  return new Promise(resolve => {
    if (typeof document === 'undefined') {
      resolve(null)
      return
    }
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.html,text/html'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) {
        resolve(null)
        return
      }
      const text = await file.text()
      resolve({text, fileName: file.name})
    }
    input.oncancel = () => resolve(null)
    input.click()
  })
}

export function HtmlBridgeEditor({
  capsule,
  onSettingsUpdate,
}: ThemeEditorProps) {
  const settings = capsule.behaviorSettings ?? {}
  const customHtml = (settings.customHtml as string) || ''
  const customHtmlFileName = (settings.customHtmlFileName as string) || ''

  const [urlInput, setUrlInput] = useState(
    (settings.htmlThemeUrl as string) ?? '',
  )
  const [previewSrc, setPreviewSrc] = useState<string | null>(() => {
    if (customHtml)
      return URL.createObjectURL(new Blob([customHtml], {type: 'text/html'}))
    return (settings.htmlThemeUrl as string) || null
  })
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [uploaded, setUploaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const blobRef = useRef<string | null>(null)
  const previewIframeRef = useRef<HTMLIFrameElement | null>(null)
  // Track whether we've sent init to the current preview iframe load.
  const previewInitSentRef = useRef(false)
  // Track current slide index for host-driven navigation (navigate_next/prev/to).
  const previewSlideIndexRef = useRef(0)

  // -- Bridge protocol for the editor preview iframe -------------------------
  // Responds to the theme's protocol messages so the teacher sees real slides
  // in the preview panel. Uses mapSlide (same as HtmlBridgePlayer) so every
  // BridgeSlide field is normalised identically. Navigation is fully handled
  // so host-driven themes (navigate_next/prev/to -> slide_show) also work here.
  useEffect(() => {
    if (Platform.OS !== 'web') return

    const handleMessage = (event: MessageEvent) => {
      if (!previewIframeRef.current) return
      if (event.source !== previewIframeRef.current.contentWindow) return
      const msg = event.data as {type?: string; [k: string]: unknown}
      if (!msg || typeof msg.type !== 'string') return

      // -- Theme ready: send init + slide 0 ----------------------------------
      if (msg.type === 'ready' && !previewInitSentRef.current) {
        previewInitSentRef.current = true
        previewSlideIndexRef.current = 0
        const slides = (capsule.slides ?? []).map(mapSlide)
        const totalPoints = slides.reduce(
          (s, sl) => s + (sl.isExercise ? sl.points : 0),
          0,
        )

        previewIframeRef.current?.contentWindow?.postMessage(
          {
            type: 'init',
            payload: {
              protocolVersion: 3,
              mode: 'creator',
              locale: 'en',
              isRtl: false,
              user: {
                id: 'preview',
                handle: '@preview',
                displayName: 'Preview',
                avatarUri: null,
              },
              behaviour: {
                instantFeedback: true,
                shuffleOptions: false,
                showProgressBar: true,
              },
              capsule: {
                id: capsule.id,
                title: capsule.title || 'Preview',
                description: capsule.description || '',
                authorHandle: '@preview',
                authorDisplayName: 'Preview',
              },
              slides,
              totalSlides: slides.length,
              totalPoints,
            },
          },
          '*',
        )

        if (slides.length > 0) {
          previewIframeRef.current?.contentWindow?.postMessage(
            {
              type: 'slide_show',
              payload: {index: 0, slide: slides[0], total: slides.length},
            },
            '*',
          )
        }
      }

      // -- Host-driven navigation: reply with slide_show ---------------------
      // Themes that call navigate_next/prev/to and await slide_show from the
      // host now work in the editor preview, not only in student play.
      if (
        msg.type === 'navigate_next' ||
        msg.type === 'navigate_prev' ||
        msg.type === 'navigate_to'
      ) {
        const slides = (capsule.slides ?? []).map(mapSlide)
        let nextIdx = previewSlideIndexRef.current
        if (msg.type === 'navigate_next')
          nextIdx = Math.min(nextIdx + 1, slides.length - 1)
        else if (msg.type === 'navigate_prev')
          nextIdx = Math.max(nextIdx - 1, 0)
        else if (msg.type === 'navigate_to') {
          const {index} = (msg.payload ?? {}) as {index?: number}
          if (typeof index === 'number')
            nextIdx = Math.max(0, Math.min(index, slides.length - 1))
        }
        previewSlideIndexRef.current = nextIdx
        const sl = slides[nextIdx]
        if (sl && previewIframeRef.current?.contentWindow) {
          previewIframeRef.current.contentWindow.postMessage(
            {
              type: 'slide_show',
              payload: {index: nextIdx, slide: sl, total: slides.length},
            },
            '*',
          )
        }
      }

      // -- Answer: echo feedback so the theme UI doesn't hang ----------------
      if (msg.type === 'answer') {
        const p = msg.payload as {
          slideId?: string
          value?: unknown
          timeSpentMs?: number
        } | null
        if (p?.slideId) {
          previewIframeRef.current?.contentWindow?.postMessage(
            {
              type: 'feedback',
              payload: {
                slideId: p.slideId,
                correct: true,
                pointsEarned: 0,
                pointsPossible: 0,
                explanation: 'Creator preview - answers not graded.',
              },
            },
            '*',
          )
        }
      }

      // -- Complete: send a mock result so the result screen can render ------
      if (msg.type === 'complete') {
        const answerSlides = (capsule.slides ?? []).map(sl => ({id: sl.id}))
        previewIframeRef.current?.contentWindow?.postMessage(
          {
            type: 'result',
            payload: {
              totalPoints: 0,
              maxPoints: 0,
              percentage: 100,
              correctCount: 0,
              totalExerciseCount: 0,
              totalTimeMs: 0,
              answers: answerSlides.map(sl => ({
                slideId: sl.id,
                correct: true,
                pointsEarned: 0,
                value: null,
                timeSpentMs: 0,
              })),
            },
          },
          '*',
        )
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [capsule])

  // When previewSrc changes (new file/URL), reset the init guard and slide
  // index so the next load triggers a fresh init handshake from slide 0.
  useEffect(() => {
    previewInitSentRef.current = false
    previewSlideIndexRef.current = 0
  }, [previewSrc])

  const saveUrl = () => {
    const trimmed = urlInput.trim()
    // Allow absolute https/http URLs, and relative /themes/ paths (built-in themes served
    // from the app's own public directory).
    const isAbsolute =
      trimmed.startsWith('https://') || trimmed.startsWith('http://')
    const isBuiltinPath = trimmed.startsWith('/themes/')
    if (trimmed && !isAbsolute && !isBuiltinPath) {
      setError('Enter a valid https:// URL to a hosted HTML theme.')
      return
    }
    setError(null)
    onSettingsUpdate({...settings, htmlThemeUrl: trimmed || undefined})
    setPreviewSrc(trimmed || null)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  /** Uploads a .html file, persists its raw source with the capsule, and previews it. */
  const handleUploadFile = async () => {
    setBusy(true)
    setError(null)
    try {
      const picked = await pickHtmlFileTextWeb()
      if (picked) {
        if (blobRef.current) {
          URL.revokeObjectURL(blobRef.current)
          blobRef.current = null
        }
        // If the uploaded file matches a built-in theme, use the public URL
        // instead of storing raw HTML -- ensures the latest fixed version is used.
        const builtinUrl = BUILTIN_THEME_FILENAME_MAP[picked.fileName]
        if (builtinUrl) {
          setPreviewSrc(builtinUrl)
          setUrlInput(builtinUrl)
          onSettingsUpdate({
            ...settings,
            htmlThemeUrl: builtinUrl,
            customHtml: undefined,
            customHtmlFileName: undefined,
          })
        } else {
          const url = URL.createObjectURL(
            new Blob([picked.text], {type: 'text/html'}),
          )
          blobRef.current = url
          setPreviewSrc(url)
          onSettingsUpdate({
            ...settings,
            customHtml: picked.text,
            customHtmlFileName: picked.fileName,
          })
        }
        setUploaded(true)
        setTimeout(() => setUploaded(false), 2000)
      }
    } catch {
      setError('Could not read that file. Make sure it is a valid .html file.')
    } finally {
      setBusy(false)
    }
  }

  const handleClearUpload = () => {
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current)
      blobRef.current = null
    }
    onSettingsUpdate({
      ...settings,
      customHtml: undefined,
      customHtmlFileName: undefined,
    })
    setPreviewSrc((settings.htmlThemeUrl as string) || null)
  }

  // Native: not supported
  if (Platform.OS !== 'web') {
    return (
      <View style={s.placeholder}>
        <Text style={s.icon}>🌐</Text>
        <Text style={s.title}>HTML Theme Editor</Text>
        <Text style={s.desc}>
          Configure this theme in the web version of the app.{'\n'}
          HTML themes render in the browser only.
        </Text>
      </View>
    )
  }

  return (
    <View style={s.root}>
      {/* Configuration panel */}
      <View style={s.panel}>
        <Text style={s.sectionTitle}>🎨 HTML Theme Configuration</Text>

        {/* Built-in theme gallery */}
        <Text style={s.label}>Built-in themes</Text>
        <Text style={s.hint}>
          Select a built-in theme to always use the latest fixed version -- no
          upload needed.
        </Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{marginBottom: 12}}>
          <View style={{flexDirection: 'row', gap: 8}}>
            {BUILTIN_HTML_THEMES.map(theme => {
              const currentUrl = (settings.htmlThemeUrl as string) || ''
              const isActive = currentUrl === theme.path
              return (
                <TouchableOpacity
                  accessibilityRole="button"
                  key={theme.id}
                  style={[s.builtinCard, isActive && s.builtinCardActive]}
                  onPress={() => {
                    if (blobRef.current) {
                      URL.revokeObjectURL(blobRef.current)
                      blobRef.current = null
                    }
                    setPreviewSrc(theme.path)
                    setUrlInput(theme.path)
                    setSaved(false)
                    setUploaded(false)
                    onSettingsUpdate({
                      ...settings,
                      htmlThemeUrl: theme.path,
                      customHtml: undefined,
                      customHtmlFileName: undefined,
                    })
                  }}>
                  <Text style={{fontSize: 20}}>{theme.emoji}</Text>
                  <Text style={[s.builtinName, isActive && {color: '#14b8a6'}]}>
                    {theme.name}
                  </Text>
                </TouchableOpacity>
              )
            })}
          </View>
        </ScrollView>

        <View style={s.divider} />

        <Text style={s.label}>Hosted theme URL</Text>
        <View style={s.urlRow}>
          <TextInput
            accessibilityLabel="HTML theme URL"
            accessibilityHint="Enter the URL of a hosted HTML theme"
            style={s.urlInput}
            value={urlInput}
            onChangeText={v => {
              setUrlInput(v)
              setSaved(false)
            }}
            placeholder="https://my-themes.example.com/theme.html"
            placeholderTextColor="rgba(255,255,255,0.3)"
            autoCapitalize="none"
            autoCorrect={false}
            onSubmitEditing={saveUrl}
            returnKeyType="go"
          />
          <TouchableOpacity
            accessibilityRole="button"
            style={[s.saveBtn, saved && s.savedBtn]}
            onPress={saveUrl}>
            <Text style={s.saveBtnTxt}>{saved ? '✓ Saved' : 'Save URL'}</Text>
          </TouchableOpacity>
        </View>

        <View style={s.divider} />

        <Text style={s.label}>Upload HTML theme</Text>
        <Text style={s.hint}>
          Upload a .html file built with the bridge protocol. It is saved with
          this capsule, so create, preview, and play all use it -- no hosting
          required.
        </Text>
        {customHtml ? (
          <View style={s.uploadedRow}>
            <View style={{flex: 1}}>
              <Text style={s.uploadedName} numberOfLines={1}>
                📄 {customHtmlFileName || 'uploaded-theme.html'}
              </Text>
              <Text style={s.uploadedMeta}>
                {(customHtml.length / 1024).toFixed(1)} KB · saved
              </Text>
            </View>
            <TouchableOpacity
              accessibilityRole="button"
              onPress={handleClearUpload}
              style={s.clearUploadBtn}>
              <Text style={s.clearUploadBtnTxt}>Remove</Text>
            </TouchableOpacity>
          </View>
        ) : null}
        <TouchableOpacity
          accessibilityRole="button"
          style={[s.fileBtn, uploaded && s.savedBtn]}
          onPress={handleUploadFile}
          disabled={busy}>
          {busy ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : (
            <Text style={[s.fileBtnTxt, uploaded && {color: '#fff'}]}>
              {uploaded
                ? '✓ Uploaded & saved'
                : customHtml
                  ? '📂 Replace .html file'
                  : '📂 Upload .html file'}
            </Text>
          )}
        </TouchableOpacity>

        {error ? <Text style={s.error}>{error}</Text> : null}

        <View style={s.divider} />

        <Text style={s.tip}>
          💡 Create themes using the structure spec:{'\n'}
          docs/html-theme-structure.md
        </Text>
      </View>

      {/* Live preview */}
      {previewSrc ? (
        <View style={s.previewContainer}>
          <View style={s.previewHeader}>
            <Text style={s.previewLabel}>Preview</Text>
            <TouchableOpacity
              accessibilityRole="button"
              onPress={() => setPreviewSrc(null)}
              style={s.clearBtn}>
              <Text style={s.clearBtnTxt}>Clear</Text>
            </TouchableOpacity>
          </View>
          {/* @ts-ignore web-only */}
          <iframe
            ref={previewIframeRef}
            src={previewSrc}
            style={{flex: 1, border: 'none', width: '100%'}}
            sandbox="allow-scripts allow-same-origin allow-forms"
            allow="fullscreen"
            allowFullScreen
            title="HTML Theme Preview"
            onLoad={() => {
              // Race-condition fallback: if the theme fired `ready` before our
              // message listener registered, send init proactively after 300 ms.
              setTimeout(() => {
                if (
                  !previewInitSentRef.current &&
                  previewIframeRef.current?.contentWindow
                ) {
                  previewInitSentRef.current = true
                  previewSlideIndexRef.current = 0
                  const slides = (capsule.slides ?? []).map(mapSlide)
                  const totalPoints = slides.reduce(
                    (s, sl) => s + (sl.isExercise ? sl.points : 0),
                    0,
                  )
                  previewIframeRef.current.contentWindow.postMessage(
                    {
                      type: 'init',
                      payload: {
                        protocolVersion: 3,
                        mode: 'creator',
                        locale: 'en',
                        isRtl: false,
                        user: {
                          id: 'preview',
                          handle: '@preview',
                          displayName: 'Preview',
                          avatarUri: null,
                        },
                        behaviour: {
                          instantFeedback: true,
                          shuffleOptions: false,
                          showProgressBar: true,
                        },
                        capsule: {
                          id: capsule.id,
                          title: capsule.title || 'Preview',
                          description: capsule.description || '',
                          authorHandle: '@preview',
                          authorDisplayName: 'Preview',
                        },
                        slides,
                        totalSlides: slides.length,
                        totalPoints,
                      },
                    },
                    '*',
                  )
                  if (slides.length > 0) {
                    previewIframeRef.current.contentWindow.postMessage(
                      {
                        type: 'slide_show',
                        payload: {
                          index: 0,
                          slide: slides[0],
                          total: slides.length,
                        },
                      },
                      '*',
                    )
                  }
                }
              }, 300)
            }}
          />
        </View>
      ) : (
        <View style={s.previewEmpty}>
          <Text style={s.previewEmptyIcon}>🖼️</Text>
          <Text style={s.previewEmptyTxt}>
            Enter a URL or pick a file above to preview the theme
          </Text>
        </View>
      )}
    </View>
  )
}

const s = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#0a1220',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    display: 'flex' as any,
    flexDirection: 'row',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any,
  panel: {
    width: 340,
    padding: 20,
    gap: 10,
    borderRightWidth: 1,
    borderRightColor: 'rgba(255,255,255,0.08)',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overflowY: 'auto' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any,
  sectionTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 2,
  },
  hint: {color: 'rgba(255,255,255,0.5)', fontSize: 13, lineHeight: 20},
  label: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    fontWeight: '600',
    marginTop: 6,
  },
  urlRow: {flexDirection: 'row', gap: 8, alignItems: 'center'},
  urlInput: {
    flex: 1,
    height: 42,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    color: '#fff',
    fontSize: 13,
  },
  saveBtn: {
    height: 42,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: '#0085ff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  savedBtn: {backgroundColor: '#22c55e'},
  saveBtnTxt: {color: '#fff', fontSize: 13, fontWeight: '700'},
  fileBtn: {
    height: 42,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileBtnTxt: {color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: '600'},
  uploadedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(20,184,166,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(20,184,166,0.35)',
    borderRadius: 10,
    padding: 10,
  },
  uploadedName: {color: '#fff', fontSize: 13, fontWeight: '600'},
  uploadedMeta: {color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 2},
  clearUploadBtn: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: 'rgba(239,68,68,0.15)',
  },
  clearUploadBtnTxt: {color: '#f87171', fontSize: 12, fontWeight: '700'},
  builtinCard: {
    alignItems: 'center',
    gap: 4,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(255,255,255,0.04)',
    minWidth: 80,
  },
  builtinCardActive: {
    borderColor: '#14b8a6',
    backgroundColor: 'rgba(20,184,166,0.1)',
  },
  builtinName: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 11,
    fontWeight: '700',
    textAlign: 'center' as const,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.07)',
    marginVertical: 2,
  },
  tip: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 12,
    lineHeight: 18,
    backgroundColor: 'rgba(255,255,255,0.04)',
    padding: 12,
    borderRadius: 8,
  },
  error: {
    color: '#f87171',
    fontSize: 13,
    backgroundColor: 'rgba(239,68,68,0.1)',
    padding: 10,
    borderRadius: 8,
  },

  // Preview
  previewContainer: {
    flex: 1,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    display: 'flex' as any,
    flexDirection: 'column',
  },
  previewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.07)',
  },
  previewLabel: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    fontWeight: '600',
  },
  clearBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.07)',
  },
  clearBtnTxt: {color: 'rgba(255,255,255,0.5)', fontSize: 12},
  previewEmpty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 40,
  },
  previewEmptyIcon: {fontSize: 40},
  previewEmptyTxt: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 20,
  },

  // Native placeholder
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    gap: 14,
    backgroundColor: '#0a1220',
  },
  icon: {fontSize: 48},
  title: {color: '#fff', fontSize: 20, fontWeight: '800', textAlign: 'center'},
  desc: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 14,
    lineHeight: 22,
    textAlign: 'center',
  },
})
